import { Loader2 } from "lucide-react";
import { useForm } from "react-hook-form";
import { toast } from "sonner";
import { z } from "zod";

import { Button } from "@/components/ui/button";
import {
	Card,
	CardContent,
	CardDescription,
	CardFooter,
	CardHeader,
	CardTitle
} from "@/components/ui/card";
import {
	Form,
	FormControl,
	FormField,
	FormItem,
	FormLabel,
	FormMessage
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue
} from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import env from "@/valid-env";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";

export const Route = createFileRoute("/vendor-registration")({
	component: VendorRegistration
});

const NIGERIAN_STATES = [
	"Abia",
	"Adamawa",
	"Akwa Ibom",
	"Anambra",
	"Bauchi",
	"Bayelsa",
	"Benue",
	"Borno",
	"Cross River",
	"Delta",
	"Ebonyi",
	"Edo",
	"Ekiti",
	"Enugu",
	"FCT",
	"Gombe",
	"Imo",
	"Jigawa",
	"Kaduna",
	"Kano",
	"Katsina",
	"Kebbi",
	"Kogi",
	"Kwara",
	"Lagos",
	"Nasarawa",
	"Niger",
	"Ogun",
	"Ondo",
	"Osun",
	"Oyo",
	"Plateau",
	"Rivers",
	"Sokoto",
	"Taraba",
	"Yobe",
	"Zamfara"
];

const BUSINESS_CATEGORIES = [
	"Fashion",
	"Food & Groceries",
	"Electronics",
	"Gadgets",
	"Beauty & Personal Care",
	"Home & Furniture",
	"Health & Wellness",
	"Books & Stationery",
	"Other"
];

const vendorRegistrationSchema = z.object({
	businessName: z.string().min(1, "Business name is required"),
	ownerName: z.string().min(1, "Owner's name is required"),
	email: z.string().email("Invalid email address"),
	phone: z.string().min(10, "Phone number must be at least 10 digits"),
	category: z.string().min(1, "Business category is required"),
	state: z.string().min(1, "State is required"),
	address: z.string().min(1, "Business address is required"),
	instagram: z.string().optional(),
	facebook: z.string().optional(),
	description: z.string().optional()
});

type VendorRegistrationFormData = z.infer<typeof vendorRegistrationSchema>;

const responseSchema = z.object({
	success: z.boolean(),
	error: z.string().optional(),
	message: z.string().optional()
});

async function submitToGoogleSheets(data: VendorRegistrationFormData) {
	try {
		const response = await fetch(env.VITE_GOOGLE_APPS_SCRIPT_URL, {
			method: "POST",
			redirect: "follow",
			headers: {
				"Content-Type": "text/plain;charset=utf-8"
			},
			body: JSON.stringify(data)
		});

		if (!response.ok) {
			throw new Error("Failed to submit data");
		}

		const da = await response.json();

		const parsedData = responseSchema.parse(da);

		return parsedData;
	}
	catch (error) {
		console.error("Network error:", error);
		throw new Error(
			"Failed to submit data. Please check your internet connection."
		);
	}
}

export function VendorRegistration() {
	return (
		<main className="min-h-screen bg-gray-50 flex flex-col items-center justify-center p-4 md:p-8">
			<div className="w-full max-w-4xl">
				<div className="flex flex-col items-center mb-8">
					<div className="bg-primary rounded-full p-4 mb-4">
						<img
							src="/intasida-icon.svg"
							alt="intasida"
							width={100}
							height={100}
							className="w-10 h-10"
						/>
					</div>
					<h1 className="text-3xl font-bold text-center text-gray-900">
						Intasida
					</h1>
					<p className="text-gray-600 mt-2 text-center">
						Vendor Registration Portal
					</p>
				</div>
				<VendorRegistrationForm />
			</div>
		</main>
	);
}

export function VendorRegistrationForm() {
	const form = useForm<VendorRegistrationFormData>({
		resolver: zodResolver(vendorRegistrationSchema),
		defaultValues: {
			businessName: "",
			ownerName: "",
			email: "",
			phone: "",
			category: "",
			state: "",
			address: "",
			instagram: "",
			facebook: "",
			description: ""
		}
	});

	const submitMutation = useMutation({
		mutationFn: submitToGoogleSheets,
		onSettled: () => {
			toast.success("Your vendor application has been submitted successfully.");
			form.reset();
			// if (data?.success) {
			// }
			// else {
			// 	toast.error(data?.message || "There was an error submitting your application. Please try again.");
			// 	throw new Error(data?.message || "There was an error submitting your application. Please try again.");
			// }
		}
	});

	const onSubmit = async (data: VendorRegistrationFormData) => {
		await submitMutation.mutateAsync(data);
	};

	return (
		<Card className="w-full">
			<CardHeader>
				<CardTitle>Vendor Registration</CardTitle>
				<CardDescription>
					Complete the form below to register as a vendor and start accepting
					payments through intasida.
				</CardDescription>
			</CardHeader>
			<Form {...form}>
				<form onSubmit={form.handleSubmit(onSubmit)}>
					<CardContent className="space-y-6">
						<div className="space-y-4">
							<h3 className="text-lg font-medium">Business Information</h3>

							<div className="grid gap-4">
								<FormField
									control={form.control}
									name="businessName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Business Name *</FormLabel>
											<FormControl>
												<Input {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="ownerName"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Owner's Full Name *</FormLabel>
											<FormControl>
												<Input {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<FormField
								control={form.control}
								name="category"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Business Category *</FormLabel>
										<Select
											onValueChange={field.onChange}
											defaultValue={field.value}
										>
											<FormControl>
												<SelectTrigger>
													<SelectValue placeholder="Select business category" />
												</SelectTrigger>
											</FormControl>
											<SelectContent>
												{BUSINESS_CATEGORIES.map(category => (
													<SelectItem
														key={category}
														value={category.toLowerCase()}
													>
														{category}
													</SelectItem>
												))}
											</SelectContent>
										</Select>
										<FormMessage />
									</FormItem>
								)}
							/>

							<FormField
								control={form.control}
								name="description"
								render={({ field }) => (
									<FormItem>
										<FormLabel>Business Description</FormLabel>
										<FormControl>
											<Textarea
												{...field}
												placeholder="Briefly describe your business and the products/services you offer"
												className="min-h-[100px] placeholder:text-sm"
											/>
										</FormControl>
										<FormMessage />
									</FormItem>
								)}
							/>
						</div>

						<div className="space-y-4">
							<h3 className="text-lg font-medium">Contact Information</h3>

							<div className="grid gap-4 md:grid-cols-2">
								<FormField
									control={form.control}
									name="email"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Email Address *</FormLabel>
											<FormControl>
												<Input {...field} type="email" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="phone"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Phone Number *</FormLabel>
											<FormControl>
												<Input {...field} type="tel" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="grid gap-4 md:grid-cols-2">
								<FormField
									control={form.control}
									name="state"
									render={({ field }) => (
										<FormItem>
											<FormLabel>State *</FormLabel>
											<Select
												onValueChange={field.onChange}
												defaultValue={field.value}
											>
												<FormControl>
													<SelectTrigger>
														<SelectValue placeholder="Select state" />
													</SelectTrigger>
												</FormControl>
												<SelectContent>
													{NIGERIAN_STATES.map(state => (
														<SelectItem key={state} value={state.toLowerCase()}>
															{state}
														</SelectItem>
													))}
												</SelectContent>
											</Select>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="address"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Business Address *</FormLabel>
											<FormControl>
												<Input {...field} />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>

							<div className="grid gap-4 md:grid-cols-2">
								<FormField
									control={form.control}
									name="instagram"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Instagram Handle</FormLabel>
											<FormControl>
												<Input {...field} placeholder="@yourbusiness" />
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>

								<FormField
									control={form.control}
									name="facebook"
									render={({ field }) => (
										<FormItem>
											<FormLabel>Facebook Page</FormLabel>
											<FormControl>
												<Input
													{...field}
													placeholder="facebook.com/yourbusiness"
												/>
											</FormControl>
											<FormMessage />
										</FormItem>
									)}
								/>
							</div>
						</div>
					</CardContent>
					<CardFooter>
						<Button type="submit" className="w-full" disabled={submitMutation.isPending}>
							{submitMutation.isPending
								? (
										<>
											<Loader2 className="mr-2 h-4 w-4 animate-spin" />
											Processing...
										</>
									)
								: (
										"Submit Registration"
									)}
						</Button>
					</CardFooter>
				</form>
			</Form>
		</Card>
	);
}
